import React, { Component } from 'react';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import { sortBy, findIndex } from 'lodash';
import moment from 'moment';

// material
import { Container, Grid, Hidden } from '@material-ui/core';
import { withStyles, Theme } from '@material-ui/core/styles';
// app
import { openSnackbar, closeSnackbar } from '../../store/action-creators';
// containers
import AppContainer from '../../containers/App';
// models
import { Auth, Session } from '../../entities/auth';
import { MessageGroup as MessageGroupEntity, emptyMessageGroup, Message } from '../../entities/notification';
import { Campaign, CampaignContact, emptyCampaignContact, emptyCampaign } from '../../entities/site';
import { Address, emptyAddress } from '../../entities/home';
// components
import Loading from '../../components/site/Loading';
import BackButton from '../../components/buttons/BackButton';
import MessageHeader from './components/MessageHeader';
import MessageGroup from './components/MessageGroup';
import PropertyHeader from './components/PropertyHeader';
import Property from './components/Property';
import CampaignContactNavigate from './components/CampaignContactNavigate';

import EditCampaignContact from '../../components/campaign/EditCampaignContact';
// services
import AuthService from '../../services/auth';
import UtilsService from '../../services/utils';
import CampaignService from '../../services/campaign';

const styles: any = (theme: Theme) => ({
  title: {
    fontSize: "24px",
    fontFamily: "Roboto Medium",
    [theme.breakpoints.down('sm')]: {
      fontSize: "18px"
    }
  },
  date: {
    fontSize: 16,
    color: '#9db0be',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: 10
    }
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
    padding: "20px 5px",
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
      padding: 0,
      paddingBottom: 10
    }
  },
  container: {
    marginBottom: "100px",
    padding: 10
  }
})

interface CampaignContactProps {
  match?: any;
  location?: any;
  history: any;
  auth: Auth;
  session: Session;
  openSnackbar(message: string): void;
  closeSnackbar(): void;
}

interface CampaignContactState {
  loading: boolean;
  campaign: Campaign;
  campaignContact: CampaignContact;
  campaignContactPrevious: CampaignContact;
  campaignContactNext: CampaignContact;
  type: string;
  messageGroup: MessageGroupEntity,
  messages: Message[];
  address: Address,
  backText: string;
  backUrl: string;
  openEditCampaignContact: boolean;
  campaignContactId: string;
}

class CampaignContactPage extends Component<CampaignContactProps, CampaignContactState> {

  constructor(props: CampaignContactProps) {
    super(props);
    this.state = {
      loading: false,
      campaign: emptyCampaign,
      campaignContact: emptyCampaignContact,
      campaignContactPrevious: emptyCampaignContact,
      campaignContactNext: emptyCampaignContact,
      type: '',
      messageGroup: emptyMessageGroup,
      messages: [],
      address: emptyAddress,
      backText: 'Back to campaigns',
      backUrl: '',
      openEditCampaignContact: false,
      campaignContactId: ''
    }
  }

  componentDidMount = async () => {
    const campaignId = this.props.match.params.campaignId;
    const contactId = this.props.match.params.contactId;
    const type = this.props.match.params.type;

    if ((type === 'replies' || type === 'clicks' || type === 'favorites') && this.props.session.campaignContacts) {
      let previous: CampaignContact = emptyCampaignContact;
      let next: CampaignContact = emptyCampaignContact;
      const sessionContacts = this.props.session.campaignContacts;
      const currentIndex = findIndex(sessionContacts, { id: contactId });

      if (currentIndex > 0) {
        previous = sessionContacts[currentIndex - 1];
      }

      if ((currentIndex + 1) <= sessionContacts.length) {
        next = sessionContacts[currentIndex + 1];
      }

      this.setState({
        campaignContactPrevious: previous,
        campaignContactNext: next,
        type,
        backText: `Back to ${type}`
      })

    }

    this.fetchAll(campaignId, contactId);
  }

  fetchAll = async (campaignId: string, contactId: string) => {

    try {

      const type = this.props.match.params.type;

      this.setState({ loading: true });

      // fetch
      const campaignRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/${campaignId}`);
      const campaignContactRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/contact/${contactId}`);
      const messageGroupRes = await axios.get(`${AuthService.getApiUrls().notifications}/message/group/contact/${contactId}`);
      const messageRes = await axios.get(`${AuthService.getApiUrls().notifications}/messages/group/${messageGroupRes.data.id}`);
      let sortedMessages = sortBy(messageRes.data, 'createdDate');
      sortedMessages = sortedMessages.reverse();

      let backUrl = `/campaign/${campaignRes.data.id}`;
      if (type) {
        backUrl = `/campaign/${type}`;
      }

      this.setState({
        loading: false,
        campaign: campaignRes.data,
        campaignContact: campaignContactRes.data,
        messageGroup: messageGroupRes.data,
        messages: sortedMessages,
        backUrl
      });

    } catch (error) {
      this.setState({ loading: false });
      console.log('error', error);
    }

  }

  handleSendMessage = async (campaignContact: CampaignContact, message: string) => {

    try {

      this.setState({ loading: true });

      await CampaignService.sendMessage(
        AuthService.getApiUrls().notifications,
        this.props.auth.user,
        this.state.messageGroup.id,
        this.state.messageGroup.toPhoneNumber,
        this.state.messageGroup.fromPhoneNumber,
        message
      );

      const messageRes = await axios.get(`${AuthService.getApiUrls().notifications}/messages/group/${this.state.messageGroup.id}`);
      this.setState({
        loading: false,
        messages: messageRes.data
      });

    } catch (error) {
      console.log(error);
      this.setState({ loading: false });
    }

  }

  handlePropertyLoad = async (campaignContact: CampaignContact) => {

    try {

      this.setState({ loading: true });

      const addressStr = `${campaignContact.address}, ${campaignContact.city}, ${campaignContact.state}`;
      const addressId = UtilsService.getKeyFromAddress(addressStr);
      const addressRes = await axios.get(`${AuthService.getApiUrls().home}/address/${addressId}`);

      this.setState({
        loading: false,
        address: addressRes.data
      });

    } catch (error) {
      console.log(error);
      this.setState({ loading: true });
    }

  }

  handleNavigate = (contact: CampaignContact) => {
    this.props.history.replace(`/redirect?url=/campaign/${contact.campaignId}/contact/${contact.id}/type/${this.state.type}`);
  }

  handleRefresh = (contact: CampaignContact) => {
    this.fetchAll(this.state.campaign.id, contact.id);
  }

  handleEdit = (contact: CampaignContact) => {
    this.setState({
      campaignContactId: contact.id,
      openEditCampaignContact: true
    });
  }

  handleEditCampaignContactClose = () => {
    this.setState({openEditCampaignContact: false});
  }

  render() {
    const { classes }: any = this.props;
    const dateFormatted = moment(this.state.campaignContact.createdDate).format('MMM DD, YYYY');
    return (
      <AppContainer showOption={false} maxWidth={false}>
        <Loading open={this.state.loading} />
        <Container maxWidth="lg">
          <div className={classes.container}>
            <CampaignContactNavigate
              previous={this.state.campaignContactPrevious}
              next={this.state.campaignContactNext}
              onClick={this.handleNavigate}
            />
            <BackButton text={this.state.backText} textMobile={this.state.campaign.name} onClick={() => this.props.history.push(this.state.backUrl)} />
            <div className={classes.header}>
              <Hidden only={['xs', 'sm']}>
                {this.state.campaign.name &&
                  <div className={classes.title}>{`${this.state.campaign.name} - ${this.state.campaignContact.firstName} ${this.state.campaignContact.lastName}`}</div>
                }
              </Hidden>
              <Hidden mdUp>
                <div className={classes.title}>{this.state.campaignContact.firstName} {this.state.campaignContact.lastName}</div>
              </Hidden>
              <div className={classes.date}>{dateFormatted}</div>  
            </div>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={12} md={8}>
                <MessageHeader 
                  contact={this.state.campaignContact} 
                  campaign={this.state.campaign}
                  onEdit={this.handleEdit}
                  onRefresh={this.handleRefresh}
                  onOpen={(contact) => null}
                  onSendMessage={this.handleSendMessage} 
                />
                <MessageGroup messageGroup={this.state.messageGroup} messages={this.state.messages} />
              </Grid>
              <Hidden only={['xs', 'sm']}>
                <Grid item xs={12} sm={12} md={4}>
                  <PropertyHeader contact={this.state.campaignContact} onPropertyLoad={this.handlePropertyLoad} />
                  {this.state.address.id &&
                    <Property address={this.state.address} />
                  }
                </Grid>
              </Hidden>
            </Grid>
          </div>
        </Container>
        <EditCampaignContact
          open={this.state.openEditCampaignContact}
          campaignContactId={this.state.campaignContactId}
          onClose={this.handleEditCampaignContactClose}
        />
      </AppContainer>
    )
  }
}

const mapStateToProps = (state: any) => {
  return {
    auth: state.auth.auth,
    session: state.session.session
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators({ openSnackbar, closeSnackbar }, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter<any, any>(withStyles(styles)(CampaignContactPage)));



// WEBPACK FOOTER //
// ./src/pages/campaign/CampaignContact.tsx